<template>
  <v-card tile class="fill-height">
    <v-card tile flat class="pl-2">
      <!-- Top Toolbar -->
      <v-card-title v-if="!story" class="px-2 py-4">
        <!-- ANALYSIS -->
        <v-btn color="primary" outlined small @click="showHideAnalysis()">
          {{ showAnalysis ? 'Hide Rolling 12M Analysis' : 'Show Rolling 12M Analysis' }}
        </v-btn>
      </v-card-title>
    </v-card>

    <v-skeleton-loader v-if="loading" type="table-tbody"></v-skeleton-loader>
    <v-data-table id="top_entities" v-else :items="dataview.data" :items-per-page="10" :headers="dataview.headers"
      class="elevation-1 px-2">
      <template v-if="entity_type == 'supplier'" #item.supplier="{ item }">
        <div>
          <router-link class="font-weight-normal caption"
            :to="{ name: group.alt_value, params: { id: item[group.value].id } }">
            {{ item[group.value].name }}
          </router-link>
          <span>
            <v-tooltip top max-width="250">
              <template v-slot:activator="{ on, attrs }">
                <v-btn small @click="clicked_entity = item[group.value], profile_member = !profile_member" icon>
                  <v-icon small v-bind="attrs" v-on="on" medium color="secondary">mdi-chart-box-outline</v-icon>
                </v-btn>
              </template>
              <span>View more supplier analysis for this profile</span>
            </v-tooltip>
          </span>
        </div>
      </template>
      <template v-else-if="entity_type == 'publisher'" #item.publisher="{ item }">
        <div>
          <router-link class="font-weight-normal caption"
            :to="{ name: group.alt_value, params: { id: item[group.value].id } }">
            {{ item[group.value].name }}
          </router-link>
          <span>
            <v-tooltip top max-width="250">
              <template v-slot:activator="{ on, attrs }">
                <v-btn small @click="clicked_entity = item[group.value], profile_member = !profile_member" icon>
                  <v-icon small v-bind="attrs" v-on="on" medium color="secondary">mdi-chart-box-outline</v-icon>
                </v-btn>
              </template>
              <span>View more buyer analysis for this profile</span>
            </v-tooltip>
          </span>
        </div>
      </template>
      <template v-else-if="entity_type == 'category'" #item.category="{ item }">
        <div>
          <router-link class="font-weight-normal caption"
            :to="{ name: group.alt_value, params: { id: item[group.value].id } }">
            {{ item[group.value].name }}
          </router-link>
          <span>
            <v-tooltip top max-width="250">
              <template v-slot:activator="{ on, attrs }">
                <v-btn small @click="clicked_entity = item[group.value], profile_member = !profile_member" icon>
                  <v-icon small v-bind="attrs" v-on="on" medium color="secondary">mdi-chart-box-outline</v-icon>
                </v-btn>
              </template>
              <span>View more category analysis for this profile</span>
            </v-tooltip>
          </span>
        </div>
      </template>
      <template #item.keyword="{ item }">
        <span class="font-weight-normal caption">
          {{ item[group.value] }}
        </span>
      </template>
      <template #item.supplier_cluster="{ item }">
        <span class="font-weight-normal caption">
          {{ item[group.value] }}
        </span>
      </template>
      <template #item.buyer_cluster="{ item }">
        <span class="font-weight-normal caption">
          {{ item[group.value] }}
        </span>
      </template>
      <template #item.doc_count="{ item }">
        <router-link class="font-weight-normal caption" v-if="group.value == 'keyword'"
          :to="{ name: 'search', query: { ...$route.query, 'tender': item[group.value], viewIndex: 0 } }">
          {{ item.doc_count }}
        </router-link>
        <span class="font-weight-normal caption" v-else-if="group.value == 'supplier_cluster'">
          {{ item.doc_count }}
        </span>
        <span class="font-weight-normal caption" v-else-if="group.value == 'buyer_cluster'">
          {{ item.doc_count }}
        </span>
        <router-link class="font-weight-normal caption" v-else-if="group.value == 'government'"
          :to="{ name: 'search', query: { ...$route.query, [group.value]: item[group.value], viewIndex: 0 } }">
          {{ item.doc_count }}
        </router-link>
        <router-link class="font-weight-normal caption" v-else
          :to="{ name: 'search', query: { ...$route.query, [group.value + '_id']: item[group.value].id, [profile_type + '_id']: profile_id, viewIndex: 0 } }">
          {{ item.doc_count }}
        </router-link>
      </template>
      <template #item.value="{ item }">
        <span class="font-weight-normal caption">${{ item.value }}M</span>
      </template>

      <template #item.avg_contract="{ item }">
        <span class="font-weight-normal caption">${{ item.avg_contract }}M</span>
      </template>
      <template #item.per_awards="{ item }">
        <v-progress-linear :value="item.per_awards" color="primary" height="20">
          <template v-slot:default="{ value }">
            <strong class="font-weight-normal caption">{{ Math.ceil(value) }}%</strong>
          </template>
        </v-progress-linear>
      </template>
      <template #item.per_award_value="{ item }">
        <v-progress-linear :value="item.per_award_value" color="rgb(255, 231, 10)" height="20">
          <template v-slot:default="{ value }">
            <strong class="font-weight-normal caption">{{ Math.ceil(value) }}%</strong>
          </template>
        </v-progress-linear>
      </template>
      <template #item.current_year_total="{ item }">
        <span class="font-weight-normal caption">${{ item.current_year_total }}M</span>
      </template>
      <template #item.spend_change_percent="{ item }">
        <div class="font-weight-normal caption"
          style="color:#12253a; min-width:100px !important; text-align: left !important;">
          <v-icon :color="growth_format(item.spend_change_percent).color">
            {{ growth_format(item.spend_change_percent).icontype }}
          </v-icon>
          {{ growth_format(item.spend_change_percent).change }}%
        </div>
      </template>
      <template #item.curr_yr_avg_contract="{ item }">
        <span class="font-weight-normal caption">${{ item.curr_yr_avg_contract }}M</span>
      </template>
      <template #item.avg_change_percent="{ item }">
        <div class="font-weight-normal caption"
          style="color:#12253a; min-width:100px !important; text-align: left !important;">
          <v-icon :color="growth_format(item.avg_change_percent).color">
            {{ growth_format(item.avg_change_percent).icontype }}
          </v-icon>
          {{ growth_format(item.avg_change_percent).change }}%
        </div>
      </template>
    </v-data-table>

    <v-dialog v-model="sharebox" hide-overlay max-width="1200" absolute style="overflow-x:'hidden' !important;">
      <v-card tile>
        <v-card-title>Add leaderboard to a custom report
          <div class="flex-grow-1"></div> <v-btn icon @click="sharebox = !sharebox"><v-icon
              medium>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card tile flat class="mx-auto pa-5" max-width="1150px">
          <v-row justify="center">
            <v-col cols="6" class="pb-0 d-flex child-flex">
              <v-combobox v-model="selected_board" :items="storyboards" :loading="isLoading" item-value="id"
                :search-input.sync="search" item-text="plainTextTitle" label="Select a report" outlined dense
                class="pb-0" return-object>
              </v-combobox>
            </v-col>
            <v-col cols="6" class="pb-0">

              <v-btn color="primary" :disabled="!saveButtonActive"
                @click="saveChart(), updateBoards(), sharebox = !sharebox">Save leaderboard to report</v-btn>
            </v-col>

            <v-col cols="12" class="pt-0 d-flex child-flex">
              <story :changed="embed_obj_changed" :key="embed_obj_changed" :data="embed_object" />
            </v-col>
          </v-row>
        </v-card>
      </v-card>
    </v-dialog>

    <v-dialog v-model="profile_member" hide-overlay max-width="1000" absolute style="overflow-x:'hidden' !important;">
      <v-card tile>
        <v-card-title class="font-weight-normal subtitle-2">{{ clicked_entity.name }}
          <div class="flex-grow-1"></div> <v-btn icon @click="profile_member = !profile_member"><v-icon
              medium>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card tile flat class="mx-auto pa-5" max-width="900px">
          <v-row justify="center">
            <v-col cols="10" class="pb-0 d-flex child-flex">
              <profile-member-graph-container :entity_id="clicked_entity.id" :entity_name="clicked_entity.name"
                :profile_id="profile_id" :profile_name="profile_name" :profile_type="profile_type"
                :entity_type="entity_type" />
            </v-col>

          </v-row>
        </v-card>
      </v-card>
    </v-dialog>

  </v-card>
</template>

<script>
import { bus } from "../main.js";
import moment from "moment";
//import tableToJSON from "tabletojson";
import { growth_format, exportCSVFile, getCurrencySymbol } from "./Utils.js";
import { entitySearch } from "./APIManager.js";
import { fetchStoryboards, addStory, addStoryboard } from "./APIManager"
import Story from "./Story.vue";
import ProfileMemberGraphContainer from "./ProfileMemberGraphContainer.vue";

export default {
  props: {
    story: {
      type: Boolean,
      default: false
    },
    chart_options: Object,
    changed: Number,
    startIndex: {
      type: Number,
      default: 2
    },
    profile_id: String,
    profile_type: String,
    entity_type: String,
    profile_name: String,
    query: Object
  },
  components: {
    Story,
    ProfileMemberGraphContainer
  },

  data() {
    return {
      clicked_entity: Object,
      profile_member: false,
      isLoading: false,
      sharebox: false,
      widget_name: "leaderboard",
      entity_aggs: [],
      defaultButton: 0,
      currentIndex: 0,
      publisher_aggs: [],
      keyword_aggs: [],
      supplier_aggs: [],
      category_aggs: [],
      supplier_cluster_aggs: [],
      buyer_cluster_aggs: [],
      govt_aggs: [],
      avg_contract_value: Number,
      total_amount: Number,
      updated: 0,
      loading: Boolean,
      //row_number: [50],
      num_entities: 1000,
      total_records: Number,
      num_publishers: Number,
      num_suppliers: Number,
      num_categories: Number,
      num_keywords: Number,
      num_supplier_clusters: Number,
      num_buyer_clusters: Number,
      num_govts: Number,
      other_categories: Number,
      other_suppliers: Number,
      other_publishers: Number,
      group_options: [{ text: "Buyer", value: "publisher", alt_value: "entity" },
      { text: "Category", value: "category", alt_value: "category" },
      { text: "Supplier", value: "supplier", alt_value: "supplier" },
      { text: "Keyword", value: "keyword", alt_value: "keyword" },
      { text: "Government", value: "government", alt_value: "government" },
      { text: "Supplier Cluster", value: "supplier_cluster", alt_value: "supplier_cluster" },
      { text: "Buyer Cluster", value: "buyer_cluster", alt_value: "buyer_cluster" }],
      group: { text: "Buyer", value: "publisher", alt_value: "entity" },
      dataview: { headers: [], data: [] },
      storydata: {},
      embed_object: {},
      storyboards: [],
      selected_board: null,
      resetChart: Boolean,
      new_board: null,
      search: null,
      saveButtonActive: false,
      embed_obj_changed: Number,
      showAnalysis: false,
    };
  },

  watch: {
    $route(to, from) {
      this.refresh();
    },
    search(val, prev) {
      if (this.selected_board || this.search != null) {
        this.saveButtonActive = true;
      }
      else {
        this.saveButtonActive = false;
      }
    },
    showAnalysis(val, prev) {
      this.loadData(this.currentIndex);
    }

  },

  created() {
    //////////////////console.log("SEARCH TERMS: ", JSON.stringify(this.search_terms));
    //this.updated = (Math.random() + 10) * Math.random();
  },

  async mounted() {
    this.loading = true;
    if (this.story) {
      this.loadStory();
    } else {
      this.loadData(this.startIndex);
    }

  },

  methods: {

    growth_format,

    async updateBoards() {
      this.isLoading = true;
      this.search = null;
      this.saveButtonActive = false;

      var data = await fetchStoryboards();
      this.storyboards = data.storyboards;

      for (var i in this.storyboards) {
        this.storyboards[i]["plainTextTitle"] = this.stripHTML(this.storyboards[i]["title"]);
      }

      this.isLoading = false;

    },

    stripHTML(text) {
      let strippedText = text.replace(/(<([^>]+)>)/gi, "");
      return strippedText;
    },

    set_chart_object() {
      this.storydata["defaultButton"] = this.currentIndex;
      this.embed_object["title"] = this.title;
      this.embed_object["description"] = this.description;
      this.embed_object["chart_type"] = "leaderboard";
      this.embed_object["payload"] = { imgData: {}, options: this.storydata }
      this.embed_object["id"] = (Math.random() + Date.now()) * Math.random();
      this.embed_obj_changed = (Math.random() + Date.now()) * Math.random();
    },

    async saveChart() {

      let data = {};
      data["title"] = this.embed_object.title;
      data["description"] = this.embed_object.description;
      data["chart_type"] = this.embed_object.chart_type;
      data["payload"] = this.embed_object.payload;

      if (!this.selected_board || (this.search != this.selected_board["plainTextTitle"])) {
        //this.selected_board = this.addItem(this.search);
        await addStoryboard({ title: this.search, description: 'This report needs an overview', story: data })
      }
      else {
        await addStory(this.selected_board.id, data)
        //////////////////console.log("EXISTING REPORT: ", this.selected_board);
      }

      //////////////////console.log("LEADER BOARD PAYLOAD: ", data);

    },

    getPercent(value, total) {
      return (value / total * 100).toFixed(1)
    },

    showHideAnalysis() {
      const qry = { ...this.$route.query };
      this.showAnalysis = !this.showAnalysis;

      //////console.log(this.hideClusters);
      qry.yoy_analysis = this.showAnalysis;
    },


    buildTable() {

      //this.dataview.headers[0] = {text:"Rank", value:"rank"};
      this.dataview.headers[0] = { text: this.group.text, value: this.group.value, sortable: false };
      this.dataview.headers[1] = { text: "# Notices", value: 'doc_count', align: 'center' };
      this.dataview.headers[2] = { text: "Value", value: 'value', align: 'center' };
      this.dataview.headers[3] = { text: "Avg", value: 'avg_contract', align: 'center' };
      this.dataview.headers[4] = { text: "% Notices", value: 'per_awards', align: 'center' };
      this.dataview.headers[5] = { text: "% Value", value: 'per_award_value', align: 'center' };

      if (this.showAnalysis) {
        this.dataview.headers[6] = { text: "12M Val", value: 'current_year_total', align: 'center' };
        this.dataview.headers[7] = { text: "12M Chg", value: 'spend_change_percent', align: 'left' };
        this.dataview.headers[8] = { text: "12M Avg", value: 'curr_yr_avg_contract', align: 'center' };
        this.dataview.headers[9] = { text: "12M Avg Chg", value: 'avg_change_percent', align: 'left' };
      }

      for (var item in this.entity_aggs) {
        let grp_value = this.group.value;
        if (grp_value == "keyword" || grp_value == "government" || grp_value == "supplier_cluster" || grp_value == "buyer_cluster") {
          this.dataview.data[item] = {};
          //this.dataview.data[item]["rank"] = parseInt(item)+1;
          this.dataview.data[item][grp_value] = this.entity_aggs[item].key;
          this.dataview.data[item]["doc_count"] = this.entity_aggs[item].doc_count;
          this.dataview.data[item]["value"] = (this.entity_aggs[item].total_amount.value / 1000000).toFixed(2);
          this.dataview.data[item]["avg_contract"] = (parseFloat((this.entity_aggs[item].total_amount.value / this.entity_aggs[item].doc_count)) / 1000000).toFixed(2);
          this.dataview.data[item]["per_awards"] = this.getPercent(this.entity_aggs[item].doc_count, this.total_records);
          if (this.total_amount < 1) {
            this.dataview.data[item]["per_award_value"] = 'N/A';
          }
          else {
            this.dataview.data[item]["per_award_value"] = this.getPercent(this.entity_aggs[item].total_amount.value, this.total_amount);
          }

          if (this.showAnalysis) {
            this.dataview.data[item]["current_year_total"] = (this.entity_aggs[item].growth.current_year_total / 1000000).toFixed(2);
            this.dataview.data[item]["spend_change_percent"] = (this.entity_aggs[item].growth.spend_change_percent);
            this.dataview.data[item]["curr_yr_avg_contract"] = (this.entity_aggs[item].growth.curr_yr_avg_contract / 1000000).toFixed(2);
            this.dataview.data[item]["avg_change_percent"] = (this.entity_aggs[item].growth.avg_change_percent);
          }
        }
        else {
          let grp_id = this.entity_aggs[item].key;

          this.dataview.data[item] = {};
          //this.dataview.data[item]["rank"] = parseInt(item)+1;
          ////console.log(this.entity_aggs[item][grp_value].buckets[0].key);
          this.dataview.data[item][grp_value] = { id: grp_id, name: this.entity_aggs[item][grp_value].buckets[0].key };
          this.dataview.data[item]["doc_count"] = this.entity_aggs[item][grp_value].buckets[0].doc_count;
          this.dataview.data[item]["value"] = (this.entity_aggs[item][grp_value].buckets[0].total_amount.value / 1000000).toFixed(2);
          this.dataview.data[item]["avg_contract"] = (parseFloat((this.entity_aggs[item][grp_value].buckets[0].total_amount.value / this.entity_aggs[item][grp_value].buckets[0].doc_count)) / 1000000).toFixed(2);
          this.dataview.data[item]["per_awards"] = this.getPercent(this.entity_aggs[item][grp_value].buckets[0].doc_count, this.total_records);
          if (this.total_amount < 1) {
            this.dataview.data[item]["per_award_value"] = 'N/A';
          }
          else {
            this.dataview.data[item]["per_award_value"] = this.getPercent(this.entity_aggs[item][grp_value].buckets[0].total_amount.value, this.total_amount);
          }

          if (this.showAnalysis) {
            this.dataview.data[item]["current_year_total"] = (this.entity_aggs[item][grp_value].buckets[0].growth.current_year_total / 1000000).toFixed(2);
            this.dataview.data[item]["spend_change_percent"] = (this.entity_aggs[item][grp_value].buckets[0].growth.spend_change_percent);
            this.dataview.data[item]["curr_yr_avg_contract"] = (this.entity_aggs[item][grp_value].buckets[0].growth.curr_yr_avg_contract / 1000000).toFixed(2);
            this.dataview.data[item]["avg_change_percent"] = (this.entity_aggs[item][grp_value].buckets[0].growth.avg_change_percent);
          }
        }

      }

    },

    openWidget() {
      let routeData = this.$router.resolve({ name: this.widget_name, query: this.$route.query });
      window.open(routeData.href, '_blank');
    },

    sort(col) {
      var table, rows, switching, i, x, y, shouldSwitch;
      table = document.getElementById("top_entities").getElementsByTagName("TABLE")[0];
      //////////////////console.log(table);
      switching = true;
      /*Make a loop that will continue until
      no switching has been done:*/
      while (switching) {
        //start by saying: no switching is done:
        switching = false;
        rows = table.rows;
        /*Loop through all table rows (except the
        first, which contains table headers):*/
        for (i = 1; i < (rows.length - 1); i++) {

          //start by saying there should be no switching:
          shouldSwitch = false;
          /*Get the two elements you want to compare,
          one from current row and one from the next:*/
          x = rows[i].getElementsByTagName("TD")[col];
          y = rows[i + 1].getElementsByTagName("TD")[col];
          //check if the two rows should switch place:
          if (x.innerHTML.toLowerCase() > y.innerHTML.toLowerCase()) {
            //if so, mark as a switch and break the loop:
            shouldSwitch = true;
            break;
          }
        }
        if (shouldSwitch) {
          /*If a switch has been marked, make the switch
          and mark that a switch has been done:*/
          rows[i].parentNode.insertBefore(rows[i + 1], rows[i]);
          switching = true;
        }
      }

    },

    exportTable() {

      //need this to convert table to string representation
      var tmp = document.createElement("div");

      let table = document.querySelector('#top_entities').cloneNode(true);

      tmp.appendChild(table);

      const HtmlTableToJson = require('html-table-to-json');

      var jsonTable = HtmlTableToJson.parse(tmp.innerHTML);

      var headers = {
        entity: 'Buyer/Supplier/Category/Keyword/Supplier Cluster/Buyer Cluster',
        notices: '# Notices',
        value: getCurrencySymbol() + ' Value',
        avg_contract: 'Avg Contract',
        per_awards: '% Awards',
        per_value: '% Award Value',
        yoy_total: getCurrencySymbol() + ' YoY Total',
        yoy_change: getCurrencySymbol() + ' YoY Change',
        yoy_avg: getCurrencySymbol() + ' YoY Avg',
        yoy_avg_change: getCurrencySymbol() + ' YoY Avg Change',
      };

      try {
        var dte = new Date();

        var fileTitle = "tendertrace_leaderboard" + "_" + dte.toISOString();
        exportCSVFile(headers, jsonTable.results[0], fileTitle);

      } catch (err) {
      }

    },

    loadGroup(index) {
      //console.log("reloading")
      //////////////console.log("loading group: ", index)
      //////////////console.log("this.group_options: ", this.group_options)
      this.loading = true;
      this.entity_aggs = [];
      this.dataview.headers = [];
      this.dataview.data = [];

      if (this.group_options[index].value == "category") {
        this.group = new Object;
        this.group = this.group_options[index];
        var new_index = 0;

        for (var i in this.category_aggs) {
          if (this.category_aggs[i].category.buckets.length == 0) {
            //////////////////console.log(this.entity_aggs[i]);
            continue;
          }
          else {
            this.entity_aggs[new_index] = this.category_aggs[i];
            new_index++;
          }
        }

      } else if (this.group_options[index].value == "publisher") {
        this.group = new Object;
        this.group = this.group_options[index];
        this.entity_aggs = this.publisher_aggs;
      }
      else if (this.group_options[index].value == "government") {
        this.group = new Object;
        this.group = this.group_options[index];
        var new_index = 0;

        for (var i in this.govt_aggs) {
          this.entity_aggs[new_index] = this.govt_aggs[i];
          new_index++;
        }
      }
      else if (this.group_options[index].value == "keyword") {
        this.group = new Object;
        this.group = this.group_options[index];
        var new_index = 0;

        for (var i in this.keyword_aggs) {
          if (this.keyword_aggs.length == 0) {
            //////////////////console.log(this.entity_aggs[i]);
            continue;
          }
          else {
            this.entity_aggs[new_index] = this.keyword_aggs[i];
            new_index++;
          }
        }
      }
      else if (this.group_options[index].value == "buyer_cluster") {
        this.group = new Object;
        this.group = this.group_options[index];
        var new_index = 0;

        for (var i in this.buyer_cluster_aggs) {
          if (this.buyer_cluster_aggs.length == 0) {
            //////////////////console.log(this.entity_aggs[i]);
            continue;
          }
          else {
            this.entity_aggs[new_index] = this.buyer_cluster_aggs[i];
            new_index++;
          }
        }
      }
      else if (this.group_options[index].value == "supplier_cluster") {
        this.group = new Object;
        this.group = this.group_options[index];
        var new_index = 0;

        for (var i in this.supplier_cluster_aggs) {
          if (this.supplier_cluster_aggs.length == 0) {
            //////////////////console.log(this.entity_aggs[i]);
            continue;
          }
          else {
            this.entity_aggs[new_index] = this.supplier_cluster_aggs[i];
            new_index++;
          }
        }
      }
      else {
        this.group = new Object;
        this.group = this.group_options[index];
        this.entity_aggs = this.supplier_aggs;
      }

      //////////////console.log("finished loading group...")

      this.buildTable();
      //////////////console.log("finished building table...")
      this.loading = false;
    },

    entity_selected(entity_id) {
      //var ent = JSON.stringify(entity);
      this.$router.push({ name: this.group.value, params: { id: entity_id } });
      //bus.$emit("entity-selected", entity);
    },

    async refresh() {
      this.loading = true;
      if (this.story) {
        this.loadStory();
      } else {
        await this.loadData(0);
      }
    },

    async loadData(index) {
      this.currentIndex = index;
      //var currRoute = this.$route.name;

      var query = this.query;

      this.loading = true;

      query["agg_size"] = this.num_entities;
      query["target_aggs"] = this.entity_type;
      query["yoy_analysis"] = this.showAnalysis;

      entitySearch(query, 1)
        .then(data => {
          ////////////////console.log("LEADERBOARD DATA: ", data);
          this.storydata = data;
          this.total_records = data.hits.value;
          this.num_publishers =
            data.aggregations.publisher_id.buckets.length +
            data.aggregations.publisher_id.sum_other_doc_count;
          this.num_govts = data.aggregations.government.buckets.length + data.aggregations.government.sum_other_doc_count;
          this.govt_aggs = data.aggregations.government.buckets;
          this.publisher_aggs = data.aggregations.publisher_id.buckets;
          this.other_publishers = data.aggregations.publisher_id.sum_other_doc_count;
          this.supplier_aggs = data.aggregations.supplier_id.buckets;

          if (data.aggregations.search_terms) {
            this.keyword_aggs = data.aggregations.search_terms.buckets;
            this.num_keywords = this.keyword_aggs.length;
          }
          else {
            this.keyword_aggs = [];
            this.num_keywords = Number;
          }

          if (data.aggregations.supplier_clusters) {
            this.supplier_cluster_aggs = data.aggregations.supplier_clusters.buckets;
            this.num_supplier_clusters = this.supplier_cluster_aggs.length;
          }
          else {
            this.supplier_cluster_aggs = [];
            this.num_supplier_clusters = Number;
          }

          if (data.aggregations.buyer_clusters) {
            this.buyer_cluster_aggs = data.aggregations.buyer_clusters.buckets;
            this.num_buyer_clusters = this.buyer_cluster_aggs.length;
          }
          else {
            this.buyer_cluster_aggs = [];
            this.num_buyer_clusters = Number;
          }

          this.other_suppliers = data.aggregations.supplier_id.sum_other_doc_count;
          this.num_suppliers =
            data.aggregations.supplier_id.buckets.length +
            data.aggregations.supplier_id.sum_other_doc_count;
          this.category_aggs = data.aggregations.category_id.buckets;
          this.other_categories = data.aggregations.category_id.sum_other_doc_count;
          this.num_categories =
            data.aggregations.category_id.buckets.length +
            data.aggregations.category_id.sum_other_doc_count;
          this.total_amount = data.aggregations.total_amount.value;
          //this.plain_text_query = data.plain_text_query;
          //////////////////console.log(JSON.stringify(this.gov_aggs));
          this.loadGroup(index);
          this.loading = false;
        })
        .catch(error => {
          //////////////////console.log(error);
          //this.errored = true;
          //this.overlay = false;
        });
    },

    loadStory() {

      let data = this.chart_options;

      this.total_records = data.hits.value;

      this.num_publishers =
        data.aggregations.publisher_id.buckets.length +
        data.aggregations.publisher_id.sum_other_doc_count;
      this.publisher_aggs = data.aggregations.publisher_id.buckets;
      this.other_publishers = data.aggregations.publisher_id.sum_other_doc_count;
      this.num_govts = data.aggregations.government.buckets.length + data.aggregations.government.sum_other_doc_count;
      this.govt_aggs = data.aggregations.government.buckets;
      this.supplier_aggs = data.aggregations.supplier_id.buckets;

      if (data.aggregations.search_terms) {
        this.keyword_aggs = data.aggregations.search_terms.buckets;
        this.num_keywords = this.keyword_aggs.length;
      }
      else {
        this.keyword_aggs = [];
        this.num_keywords = Number;
      }

      if (data.aggregations.supplier_clusters) {
        this.supplier_cluster_aggs = data.aggregations.supplier_clusters.buckets;
        this.num_supplier_clusters = this.supplier_cluster_aggs.length;
      }
      else {
        this.supplier_cluster_aggs = [];
        this.num_supplier_clusters = Number;
      }

      if (data.aggregations.buyer_clusters) {
        this.buyer_cluster_aggs = data.aggregations.buyer_clusters.buckets;
        this.num_buyer_clusters = this.buyer_cluster_aggs.length;

      }
      else {
        this.buyer_cluster_aggs = [];
        this.num_buyer_clusters = Number;
      }

      this.other_suppliers = data.aggregations.supplier_id.sum_other_doc_count;
      this.num_suppliers =
        data.aggregations.supplier_id.buckets.length +
        data.aggregations.supplier_id.sum_other_doc_count;
      this.category_aggs = data.aggregations.category_id.buckets;
      this.other_categories = data.aggregations.category_id.sum_other_doc_count;
      this.num_categories =
        data.aggregations.category_id.buckets.length +
        data.aggregations.category_id.sum_other_doc_count;
      this.total_amount = data.aggregations.total_amount.value;

      //this.defaultButton = 0;
      this.loadGroup(data.defaultButton);
      this.loading = false;

    },

    check_gov_name(gov) {
      if (gov === "AUS") {
        return "AUS";
      }
      else if (gov === "FED") {
        return "FED";
      }
      else if (gov === "DEF") {
        return "DEF";
      } else {
        return gov;
      }
    },

    numFormatter(num) {
      if (num > 999 && num < 1000000) {
        return (num / 1000).toFixed(1) + "K"; // convert to K for number from > 1000 < 1 million
      } else if (num > 1000000 && num < 1000000000) {
        return (num / 1000000).toFixed(1) + "M"; // convert to M for number from > 1 million
      } else if (num > 1000000000) {
        return (num / 1000000000).toFixed(1) + "B"; // convert to M for number from > 1 million
      } else if (num < 1000) {
        return num; // if value < 1000, nothing to do
      }
    },

    formattedCurrency: function (amount) {
      //////////////////console.log("AMOUNT: " + amount);
      if (!isNaN(amount)) {
        return getCurrencySymbol() + this.numFormatter(amount);
        //return getCurrencySymbol() + Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      } //else {
      //return null;
      //}
    },

    formattedCurrencyFull: function (amount) {
      //////////////////console.log("AMOUNT: " + amount);
      if (!isNaN(amount)) {
        return (
          getCurrencySymbol() +
          Math.round(amount)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        );
      } //else {
      //return null;
      //}
    },
    showHideDetails(item) {
      //this.item_details = !this.item_details;
      this.item_details.show = true;
      this.item_details.record = item;
      bus.$emit("show-hide-details", this.item_details);
    },
    formatDate(date_value) {
      return moment(date_value).format("MMMM YYYY");
    }
  },
  computed: {}
};
</script>

<style>
.v-data-table-header th {
  white-space: nowrap;
}
</style>
